/**
 * personal-best.ts — the per-device personal-best slot (B2, folded into
 * AV.W22.1 from W15's scope reveal).
 *
 * A speedtest run that beats the device's stored best DOWNLOAD throughput
 * earns a triumphant gold-arc flourish on the completion seal (the
 * `<CompleteBadge :personal-best>` easter egg). This module owns the
 * tiny persistence + comparison; the UI owns the flourish.
 *
 * It is a pure state helper — NO Vue reactivity, NO clock, NO side-effects
 * on the W19-owned completion choreography. The consumer calls `recordRun`
 * ONCE on the completion rising edge it already observes (`isComplete`),
 * reads back the boolean verdict, and hands it to the seal. The clock
 * stays W19's; this module is only the "is this a best?" oracle + the
 * `localStorage` write, reached through the canonical `typedStorage`
 * idiom (the same self-healing JSON wrapper the survey/session-expiry
 * tiers use) so the wire shape is a typed, corruption-resilient record.
 *
 * Scope: DOWNLOAD is the protagonist metric (the stage hero number), so
 * it is the canonical "best" axis — a run is a personal-best when its
 * download throughput strictly exceeds the stored best. The upload best
 * is tracked alongside for symmetry (a future "best upload" surface can
 * read it) but does NOT, on its own, trip the flourish: the flourish
 * celebrates the protagonist. A higher-is-better throughput comparison;
 * latency (lower-is-better) is intentionally NOT a best axis here — the
 * celebration is a throughput triumph, not a ping contest.
 */

import { typedStorage } from "@src/utils/typedStorage.ts";

/** The persisted personal-best record. Throughput in Mbps (the engine's
 *  canonical `speedMbps` unit), matching the result-bridge reads. */
export interface PersonalBest {
    /** Best DOWNLOAD throughput seen on this device, Mbps. */
    download: number;
    /** Best UPLOAD throughput seen on this device, Mbps. */
    upload: number;
    /** Epoch ms when the record was last beaten — for a future
     *  "set on <date>" affordance. */
    updatedAt: number;
}

/** The localStorage key. Namespaced under the speedtest prefix so the
 *  `typedStorage.clearPrefix("speedtest.")` reset path sweeps it too. */
export const PERSONAL_BEST_KEY = "speedtest.personal-best";

/** The run throughput a completion hands in for comparison. */
export interface RunThroughput {
    /** This run's download throughput, Mbps. */
    download: number;
    /** This run's upload throughput, Mbps. */
    upload: number;
}

/** The verdict `recordRun` returns. `isBest` is the protagonist signal
 *  the seal flourish reads (download strictly beat the stored best). */
export interface PersonalBestVerdict {
    /** True when this run's DOWNLOAD strictly beat the stored best — the
     *  signal the gold-arc flourish reads. */
    isBest: boolean;
    /** True when the upload best was also beaten (tracked for symmetry). */
    isUploadBest: boolean;
    /** The record AFTER this run (the new best, or the unchanged prior). */
    best: PersonalBest;
}

/** Read the stored best, or a zero record when none is set (so the first
 *  ever completed run always counts as a best). */
export function readPersonalBest(): PersonalBest {
    return typedStorage.get<PersonalBest>(PERSONAL_BEST_KEY, {
        download: 0,
        upload: 0,
        updatedAt: 0,
    });
}

/**
 * Record a completed run against the stored best and return the verdict.
 *
 * A run is a personal-best when its DOWNLOAD throughput STRICTLY exceeds
 * the stored best download — so re-running an identical result does NOT
 * re-trip the flourish (a strict `>`, never `>=`). The upload best is
 * lifted independently (a run can beat the upload best without beating
 * the download best, and vice-versa); only the download win flips the
 * protagonist `isBest` signal.
 *
 * Non-finite / non-positive throughput (an aborted or degenerate run) is
 * ignored on that axis — it can never be a "best", and it never clobbers
 * a real stored best with a zero. The write happens only when SOMETHING
 * improved, so a no-improvement run leaves localStorage untouched.
 */
export function recordRun(run: RunThroughput): PersonalBestVerdict {
    const prior = readPersonalBest();

    const dlValid = Number.isFinite(run.download) && run.download > 0;
    const ulValid = Number.isFinite(run.upload) && run.upload > 0;

    const isBest = dlValid && run.download > prior.download;
    const isUploadBest = ulValid && run.upload > prior.upload;

    if (!isBest && !isUploadBest) {
        return { isBest, isUploadBest, best: prior };
    }

    const best: PersonalBest = {
        download: isBest ? run.download : prior.download,
        upload: isUploadBest ? run.upload : prior.upload,
        updatedAt: Date.now(),
    };
    typedStorage.set(PERSONAL_BEST_KEY, best);

    return { isBest, isUploadBest, best };
}

/** Clear the stored best (test/reset affordance). */
export function clearPersonalBest(): void {
    typedStorage.remove(PERSONAL_BEST_KEY);
}
